import { motion } from "framer-motion";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";

const NotFoundPage = () => {
  const { LocalizedLink } = useLocale();
  const { medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [-2, 2, -2],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-center w-screen h-screen text-center px-4 overflow-hidden relative"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-buttonColor/20 to-transparent blur-xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-gradient-to-br from-buttonColor/15 to-transparent blur-xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 5, repeat: Infinity, delay: 1 }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-gradient-to-br from-textColor/10 to-transparent blur-lg"
          animate={{ x: [-10, 10, -10], y: [-5, 15, -5] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      {/* Main 404 Text with floating animation */}
      <motion.div variants={floatingVariants} animate="animate">
        <motion.h1
          className={cn("text-8xl md:text-9xl text-textColor relative", medium)}
          variants={itemVariants}
          style={{
            textShadow: "0 10px 30px rgba(0,0,0,0.1)",
            background: "linear-gradient(135deg, currentColor 0%, currentColor 100%)",
            WebkitBackgroundClip: "text",
          }}
        >
          4
          <motion.span className="inline-block" animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}>
            0
          </motion.span>
          4
        </motion.h1>
      </motion.div>

      {/* Subtitle with stagger animation */}
      <motion.div variants={itemVariants} className="mt-8 space-y-2">
        <motion.h2 className="text-2xl md:text-3xl font-semibold text-textColor" variants={itemVariants}>
          Page Not Found
        </motion.h2>
        <motion.p className="text-lg text-textSecondaryColor max-w-md mx-auto leading-relaxed" variants={itemVariants}>
          Oops! The page you are looking for seems to have wandered off into the digital void.
        </motion.p>
      </motion.div>

      {/* Illustration/Icon */}
      <motion.div variants={itemVariants} className="my-8">
        <motion.div className="relative w-48 h-32 mx-auto" whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 300 }}>
          {/* Simple geometric illustration */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          >
            <svg width="192" height="128" viewBox="0 0 192 128" className="text-textSecondaryColor/30">
              <motion.path
                d="M20 64 L60 24 L100 64 L140 24 L172 64"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: 0.5 }}
              />
              <motion.circle
                cx="96"
                cy="64"
                r="8"
                fill="currentColor"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 1.8 }}
              />
            </svg>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Action buttons */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mt-8">
        <LocalizedLink
          to="/"
          className="group relative inline-flex items-center justify-center bg-buttonColor rounded-full font-inter cursor-pointer text-white px-8 py-3 transition-all duration-300 hover:shadow-lg hover:shadow-buttonColor/25 hover:-translate-y-0.5 overflow-hidden"
        >
          <motion.span className="relative z-10" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            Go to Home
          </motion.span>
          <motion.div
            className="absolute inset-0 bg-white/10 rounded-full"
            initial={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </LocalizedLink>

        <motion.button
          onClick={() => window.history.back()}
          className="group inline-flex items-center justify-center border-2 border-textSecondaryColor/30 rounded-full font-inter cursor-pointer text-textColor px-8 py-3 transition-all duration-300 hover:border-buttonColor hover:shadow-lg hover:-translate-y-0.5"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.span className="flex items-center gap-2" whileHover={{ x: -2 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            Go Back
          </motion.span>
        </motion.button>
      </motion.div>

      {/* Helpful links */}
      <motion.div variants={itemVariants} className="mt-12 text-sm text-textSecondaryColor">
        <p className="mb-3">Looking for something specific?</p>
        <div className="flex flex-wrap justify-center gap-4">
          <LocalizedLink to="/about" className="hover:text-buttonColor transition-colors duration-200 underline decoration-dotted underline-offset-4">
            About Us
          </LocalizedLink>
          <LocalizedLink
            to="/contact"
            className="hover:text-buttonColor transition-colors duration-200 underline decoration-dotted underline-offset-4"
          >
            Contact
          </LocalizedLink>
          <LocalizedLink to="/help" className="hover:text-buttonColor transition-colors duration-200 underline decoration-dotted underline-offset-4">
            Help Center
          </LocalizedLink>
        </div>
      </motion.div>

      {/* Subtle animated particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-buttonColor/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              opacity: [0.2, 0.8, 0.2],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default NotFoundPage;
